import { logger } from '@/utils/logger/logger';
import {
  isVsCodePaste,
  normalizePastedFormatting,
  normalizePastedSections,
} from '@heidi/editor/core';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import type { Slice } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/react';
import {
  parsePastedMarkdown,
  type PasteParseFailure,
  type PasteParseFailureReporter,
} from './parse-pasted-markdown';

export { parsePastedMarkdown, type PasteParseFailure, type PasteParseFailureReporter };

/**
 * The app shell over the packaged pure paste seam (OpenSpec
 * `monorepo-kit-editor` tasks 4.3/4.4/5.5). Everything pure — flavor
 * classification, the private formatting normalizer, content length, the
 * canonical parser — now lives in `@heidi/editor/core`; what stays here is the
 * host-shaped remainder: paste-stage dispatch and the frozen logger trace.
 */

/**
 * Compatibility facade for the private formatting normalizer, which the door
 * exports as `normalizePastedFormatting` — the package refuses to publish a
 * `sanitize*` name for a function that is deliberately not a sanitizer (its
 * safety-shaped sibling on the door is `sanitizeHtmlAllowlisted`). The frozen
 * historical name is preserved here for the live extension and its suite.
 *
 * Not an XSS sanitizer: `<script>`, `on*` handlers and `javascript:` hrefs pass
 * through and are neutralized downstream by ProseMirror's schema parse (pinned
 * by the paste integration test). Do not reuse outside the paste pipeline.
 */
export const sanitizePastedHtml = normalizePastedFormatting;

/**
 * Set only across the synchronous `view.pasteText` re-entry below, where the
 * identical parse runs a second time on the same text. Module scope is safe
 * because it never spans an await or an event boundary: it is raised and
 * cleared inside one call, so a second editor cannot observe it raised.
 */
let suppressParseFailureWarn = false;

export { classifyPasteFlavor, pastedContentLength } from '@heidi/editor/core';

/**
 * Route every VS Code / Monaco copy through the canonical markdown parser.
 * Code blocks cannot be authored directly, so a paste must not land as one
 * giant typed code block — and simply letting these fall through is not
 * enough: VS Code also writes syntax-highlighted `text/html`, which the HTML
 * path would land as styling cruft rather than reaching
 * `clipboardTextParser`. Handling it here makes a VS Code paste behave like
 * any other plain-text paste.
 *
 * Stays app-side (task 4.4): this is paste-stage behavior — it dispatches into
 * the host view and falls back through `view.pasteText` — not a pure transform.
 */
export const handleVsCodePaste = (
  editor: Editor,
  view: EditorView,
  event: ClipboardEvent
): boolean => {
  const data = event.clipboardData;
  if (!data || !isVsCodePaste(data)) return false;
  // Inside a code block the paste stays literal, matching the parser bypass.
  if (editor.isActive('codeBlock')) return false;

  const text = data.getData('text/plain');
  if (!text) return false;
  const parsed = parseMarkdownPastedText(editor, view, text);
  if (!parsed) {
    // Returning false would hand the paste back to flavor negotiation, which
    // prefers VS Code's syntax-highlighted `text/html` — re-run ProseMirror's
    // own plain-text paste instead (it applies `transformPasted`, so section
    // normalization still runs). The bare event carries no clipboardData, so
    // this handler bows out on re-entry instead of recursing. The re-entry
    // does reach `clipboardTextParser`, which re-runs the same parse on the
    // same text and fails identically — one user paste, so one warn.
    suppressParseFailureWarn = true;
    try {
      return view.pasteText(text, new Event('paste') as ClipboardEvent);
    } finally {
      suppressParseFailureWarn = false;
    }
  }

  // `handlePaste` short-circuits `doPaste` before the slice it built through
  // `transformPasted` is used, so the shared section normalization has to run
  // here too. Without it this is the one paste path that skips it, and a `# `
  // copied into a table cell or list item strands its title as a bare heading
  // instead of flattening to a paragraph.
  const slice = normalizePastedSections(parsed, view.state.selection.$from);

  view.dispatch(
    view.state.tr.replaceSelection(slice).scrollIntoView().setMeta('paste', true)
  );
  return true;
};

/**
 * Parse pasted plain text through the canonical markdown pipeline (the kit's
 * pinned ATX-only marked instance, via `editor.markdown.parse`) so GFM tables,
 * lists, headings — and this extension's own copy output — paste as structured
 * content. Plain prose still lands as plain paragraphs.
 *
 * `openIsolating: false` keeps sections closed in the slice — defensive;
 * schema fitting re-closes them on insert anyway. Returns `null` on empty or
 * unparseable input, which lets ProseMirror's default plain-text paste run
 * instead of a broken paste. Exported for testing.
 */
export const parseMarkdownPastedText = (
  editor: Editor,
  view: EditorView,
  text: string
): Slice | null =>
  // The pure core (parse-pasted-markdown.ts, now on the `/core` door) reports
  // through the typed safe envelope; this app-side wrapper keeps the frozen
  // logger trace byte-identical: same message, same safe fields, once per
  // call.
  parsePastedMarkdown(editor, view, text, ({ errorName, textLength }) => {
    if (suppressParseFailureWarn) return;
    logger.warn('Markdown-native paste parse failed; falling back to plain text', {
      session_id: getCurrentSessionId(),
      editor_type: 'markdown_native',
      errorName,
      textLength,
    });
  });
